// theme.ts — design tokens + arrival model for the new Today header (NEW_UI).
//
// Tokens PORTED FROM THE REDUCED re:nurture app's style kit (its arrival screen +
// theme.js): warm off-white background (#F7F5F2), Fraunces titles + Inter body,
// soft pastel arrival tones, compact spacing/radius. Tiles carry on-brand SVG
// icons (this app's <Icon> set) so they don't read as bland boxes.
// Distinct abstract motifs per tile (drawn in ArrivalHeader):
//   alert      a soft warning mark            (Feeling unsafe)
//   disturb    a core being destabilised      (Emotionally triggered)
//   separate   two entities apart             (Dealing with the ex)
//   irregular  an out-of-rhythm line          (A hard week)
//   balance    balanced stacked stones        (Here to work on myself)
//   journey    a ball on a smooth curve       (Calm)
//   horizon    a sun rising over a horizon    (Rebuild)
//   hold       a shield, holding the line     (Hold)
//   paths      branching pathways/options     (Toolkit)
import type { IconName } from '@/components/icons';

export type MotifKind = 'alert' | 'disturb' | 'separate' | 'irregular' | 'balance' | 'journey' | 'horizon' | 'hold' | 'paths';

// ── Colour (reduced-app palette, incl. background) ───────────────────────────
export const C = {
  page:        '#F7F5F2',   // reduced-app warm off-white — the header band background
  surface:     '#FFFFFF',
  text:        '#1A1A1A',
  textBody:    '#374151',
  textMuted:   '#6B7280',
  textFaint:   '#9CA3AF',
  border:      '#E5E7EB',
  borderSubtle:'rgba(0,0,0,0.06)',
  brand:       '#3FA7A3',
  brandDark:   '#2E8A86',
  amber:       '#E6B325',
  amberDark:   '#B8901F',
};

export const SP = { xxs: 2, xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24 };
export const R = { sm: 10, md: 14, lg: 16, xl: 20, pill: 99 };
export const F = {
  ui:    'Inter, system-ui, -apple-system, sans-serif',
  title: 'Fraunces, Georgia, serif',
};
export const SIZE = { label: 11, caption: 12, body: 13, md: 14, lg: 15, h3: 17, h2: 22, h1: 32 };
export const W = { regular: 400, medium: 500, semibold: 600, bold: 700 };

export const eyebrow = {
  fontFamily: F.ui,
  fontSize: 11,
  fontWeight: W.bold,
  letterSpacing: '0.7px',
  textTransform: 'uppercase' as const,
};

// ── Actions → REAL existing flows (wired by ArrivalHeader's `run`). ───────────
export type ActionKey =
  | 'crisis'    // → onOpenCrisis()
  | 'calm'      // → onSettle()     emotions list → approach choice (CalmYourBodyPanel)
  | 'relax'     // → onRelax()      relaxation breathing (relax_box)
  | 'hold'      // → onHold()       the Boundary & Urge tools page
  | 'continue'   // → onContinue()   the programme feed below ("Rebuild")
  | 'compassion' // → onSelfCompassion()  switch to the self-compassion course
  | 'toolkit'    // → onOpenToolkit()
  | 'library'    // → onOpenLibrary()
  | 'care';      // → onOpenCare()   the CARE reflection (process a triggered response)

// ── Arrival tiles (PHASE 1) — ported from the reduced app's ARRIVALS. The two
//    acute tiles render emphasised on top; the rest in a grid below. ────────────
export type ArrivalId = 'unsafe' | 'triggered' | 'ex' | 'hardweek' | 'working';
export interface Arrival { id: ArrivalId; label: string; sub: string; tone: string; ink: string; acute: boolean; motif: MotifKind; }

export const ARRIVALS: Arrival[] = [
  { id: 'unsafe',    label: 'Feeling unsafe',         sub: 'Crisis support',        tone: '#f4d5d0', ink: '#5a2a23', acute: true,  motif: 'alert'     },
  { id: 'triggered', label: 'Emotionally triggered',  sub: 'Settle the body',       tone: '#d6e4ec', ink: '#2c4a59', acute: true,  motif: 'disturb'   },
  { id: 'ex',        label: 'Dealing with the ex',    sub: 'Managing contact',      tone: '#e8dec4', ink: '#5a4218', acute: false, motif: 'separate'  },
  { id: 'hardweek',  label: 'A hard week',            sub: 'Permission to do less', tone: '#dde4d6', ink: '#3c4a38', acute: false, motif: 'irregular' },
  { id: 'working',   label: 'Here to work on myself', sub: 'The deeper work',       tone: '#f1d9b4', ink: '#5a4218', acute: false, motif: 'balance'   },
];

// ── Constructed header (PHASE 2) — per-arrival variant, mapped to ActionKeys.
//    No crisis here (it's on page 1 + the help button is everywhere). ────────────
export interface Step { name: string; action: ActionKey; primary?: boolean; }
export interface Choice { label: string; action: ActionKey; }
export interface Variant {
  id: Exclude<ArrivalId, 'unsafe'>;
  tone: string; ink: string;
  headline: string; headlineEm: string; meta: string;
  cta?: Choice;
  sequence?: Step[];
  choices?: Choice[];
  // Optional copy shown once the user has real progress (e.g. the programme
  // variant warms from "Time to work on yourself" to "Pick up where you left off").
  // The header overlays these onto the base variant when `hasStarted` is true.
  returning?: Partial<Pick<Variant, 'headline' | 'headlineEm' | 'meta' | 'cta' | 'sequence' | 'choices'>>;
}

export const VARIANTS: Record<Exclude<ArrivalId, 'unsafe'>, Variant> = {
  triggered: {
    id: 'triggered', tone: '#d6e4ec', ink: '#2c4a59',
    headline: 'Calm your', headlineEm: 'body first.',
    meta: 'Settle the body first: name what you’re feeling, then choose a breath or a grounding practice. Or go straight to CARE if you’d rather process what just happened.',
    choices: [
      { label: 'Settle the body', action: 'calm' },
      { label: 'CARE', action: 'care' },
    ],
  },
  ex: {
    id: 'ex', tone: '#dfd5e6', ink: '#4a3a5e',
    headline: 'Steady yourself,', headlineEm: 'then handle it.',
    meta: 'A two-move sequence. Settle the body first, then open the boundary & urge tools to handle the contact.',
    sequence: [
      { name: 'Settle the body', action: 'calm', primary: true },
      { name: 'Hold a boundary', action: 'hold' },
    ],
  },
  hardweek: {
    id: 'hardweek', tone: '#dde4d6', ink: '#3c4a38',
    headline: 'Permission to', headlineEm: 'do less today.',
    meta: 'Survival weeks ask for less, not more. Be kind to yourself, or just breathe.',
    choices: [
      { label: 'Self-compassion', action: 'compassion' },
      { label: 'Relax', action: 'relax' },
    ],
  },
  working: {
    id: 'working', tone: '#f1d9b4', ink: '#5a4218',
    // First-use copy: never assumes prior progress.
    headline: 'Time to', headlineEm: 'work on yourself.',
    meta: 'Step into the programme at your own pace. Open the next lesson whenever you feel ready.',
    choices: [
      { label: 'Open the programme', action: 'continue' },
      { label: 'Read', action: 'library' },
    ],
    // Returning user (has real progress): warmer "carry on" framing.
    returning: {
      headline: 'Pick up', headlineEm: 'where you left off.',
      meta: 'Your recovery programme is waiting. Carry on with the next lesson whenever you feel ready.',
      choices: [
        { label: 'Continue', action: 'continue' },
        { label: 'Read', action: 'library' },
      ],
    },
  },
};

// ── The fixed menu: Calm / Rebuild / Hold / Toolkit (in that order). ───────────
export interface MenuOpt { key: string; title: string; sub: string; wash: string; ink: string; motif: MotifKind; action: ActionKey; icon?: IconName; }
export const MENU: MenuOpt[] = [
  { key: 'calm',    title: 'Calm',    sub: 'Settle the body',         wash: '#cfe0e2', ink: '#2c4a59', motif: 'journey', action: 'calm'     },
  { key: 'rebuild', title: 'Continue', sub: 'Your programme',         wash: '#ecd9b6', ink: '#5a4218', motif: 'horizon', action: 'continue', icon: 'play' },
  { key: 'hold',    title: 'Hold & Resist', sub: 'Hold your ground',   wash: '#ddd6ea', ink: '#4a3a5e', motif: 'hold',    action: 'hold'     },
  { key: 'toolkit', title: 'Toolkit', sub: 'All your tools',          wash: '#d6e2d2', ink: '#35503f', motif: 'paths',   action: 'toolkit'  },
];

// ── The Boundary & Urge tools page (opened by "Hold"). These are the tools that
//    relate to boundaries + communicating with an ex (and others). All unlocked. ─
export const HOLD_TOOL_IDS = ['beaturge', 'boundary', 'communicate', 'escalation'];
